// Single row in the Criteria Checklist. Each row
// surfaces:
//   - Item code badge (e.g. "S-DK1").
//   - Tier badge (Deal Killer / Red Flag / Needs Improvement).
//   - Status badge (Pending / Escalated / Approved / Declined / Waived).
//   - Title with optional inline <CommonExpectedNote> calibration prose.
//   - Right-aligned actions: Open form, Certify, Escalate, Decline,
//     Waive (E2 or ADMIN), and an overflow menu for Admin Reset.
//
// Action visibility honours dealFlowPermissions. Action enable state
// honours item.status (e.g. Certify hidden once status=Approved).

import { useState } from "react";
import { CheckCircle2, FileText, Lock, MoreVertical } from "lucide-react";
import { Badge, Button } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { useAuth } from "@/hooks/useAuth";
import {
  checklistItemActions,
  NO_CHECKLIST_ACTIONS,
} from "@/lib/dealFlowPermissions";
import {
  useAdminResetChecklistItem,
  useCertifyChecklistItem,
  useDeclineChecklistItem,
  useEscalateChecklistItem,
  useWaiveChecklistItem,
} from "@/hooks/dealFlow/useDealFlow";
import { useUndoCriteriaItemTerminal } from "@/hooks/dealFlow/useAdminUndo";
import { AdminUndoButton } from "../admin/AdminUndoButton";
import { useDateFormatter } from "@/utils/datetime";
import type {
  ActiveEscalationSummary,
  CriteriaCatalogItemDto,
  CriteriaChecklistItemDto,
  CriteriaItemAssessment,
} from "@/api/dealFlow/types";
import { CommonExpectedNote } from "./CommonExpectedNote";
import { CriteriaCatalogEditModal } from "./CriteriaCatalogEditModal";
import { CertifyConfirmationModal } from "./CertifyConfirmationModal";
import { DeclineModal } from "./DeclineModal";
import { EscalateModal } from "./EscalateModal";
import { WaiveModal } from "./WaiveModal";
import { AdminResetModal } from "./AdminResetModal";
import { CriteriaChecklistItemPopover } from "./CriteriaChecklistItemPopover";
import { approvedEscalationActions } from "./escalationDirective";

interface CriteriaChecklistItemRowProps {
  dealId: string;
  item: CriteriaChecklistItemDto;
  catalog: CriteriaCatalogItemDto | undefined;
  /** When true, hides write action buttons regardless of
   *  permission. The "Open form" button stays available (as read-only
   *  "View form"). */
  readOnly?: boolean;
}

export function CriteriaChecklistItemRow({
  dealId,
  item,
  catalog,
  readOnly = false,
}: CriteriaChecklistItemRowProps) {
  const toast = useToast();
  const auth = useAuth();
  const permSet = new Set(auth.user?.permissions ?? []);
  const isAdmin = auth.hasRoleCode("ADMIN");
  // What the server accepts here (owner approval, 2026-10-13); readOnly (a
  // Closed deal) turns everything off.
  const actions = readOnly
    ? NO_CHECKLIST_ACTIONS
    : checklistItemActions(permSet, {
        lane: item.lane,
        userRoleCodes: auth.user?.roleCodes ?? [],
      });

  const [formOpen, setFormOpen] = useState(false);
  const [certifyOpen, setCertifyOpen] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [escalateOpen, setEscalateOpen] = useState(false);
  const [waiveOpen, setWaiveOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [overflowOpen, setOverflowOpen] = useState(false);

  const certify = useCertifyChecklistItem(dealId);
  const escalate = useEscalateChecklistItem(dealId);
  const decline = useDeclineChecklistItem(dealId);
  const waive = useWaiveChecklistItem(dealId);
  const reset = useAdminResetChecklistItem(dealId);
  const undoTerminal = useUndoCriteriaItemTerminal(dealId, item.id);

  const surface = (verb: string) => (err: unknown) => {
    const extracted = extractErrorMessage(err);
    // The generic FALLBACK_MESSAGE inside extractErrorMessage isn't action-
    // specific; if it bubbles up here, prefer the verb-tailored fallback.
    const message =
      extracted === "Something went wrong. Please try again."
        ? `Could not ${verb}.`
        : extracted;
    toast.error(message);
  };

  const onCertify = async () => {
    try {
      await certify.mutateAsync(item.id);
      toast.success(`${item.code} certified.`);
      setCertifyOpen(false);
    } catch (err) {
      surface("certify item")(err);
    }
  };

  const onDecline = async (reason: string) => {
    try {
      await decline.mutateAsync({ itemId: item.id, body: { failureReason: reason } });
      toast.success(`${item.code} declined. Failure tracking record created.`);
      setDeclineOpen(false);
    } catch (err) {
      surface("decline item")(err);
    }
  };

  const onEscalate = async (reason: string) => {
    try {
      await escalate.mutateAsync({ itemId: item.id, escalationReason: reason });
      toast.success(`${item.code} escalated.`);
      setEscalateOpen(false);
    } catch (err) {
      surface("escalate item")(err);
    }
  };

  const onWaive = async (rationale: string) => {
    try {
      await waive.mutateAsync({ itemId: item.id, body: { rationale } });
      toast.success(`${item.code} waived.`);
      setWaiveOpen(false);
    } catch (err) {
      surface("waive item")(err);
    }
  };

  const onAdminReset = async (justification: string) => {
    try {
      // The server validates the justification and records it in the reset audit.
      await reset.mutateAsync({ itemId: item.id, reason: justification });
      toast.success(
        `${item.code} reset to Pending. (Justification: "${justification.slice(0, 80)}…")`,
      );
      setResetOpen(false);
    } catch (err) {
      surface("reset item")(err);
    }
  };

  const isTerminal =
    item.status === "Approved" ||
    item.status === "Declined" ||
    item.status === "Waived";

  // The server waives a Pending or Declined item, and an Escalated one only
  // once the escalation is approved under a directive that allows a waive
  // (owner ruling, 2026-10-26: the screen offers what the server allows).
  const waiveOffered =
    actions.waive &&
    (item.status === "Pending" ||
      item.status === "Declined" ||
      (item.status === "Escalated" && approvedEscalationActions(item).has("waive")));

  // Severity Assessment + Report-Uploaded Attestation gate
  // Certify; the attestation alone gates Decline. Source of truth is server-side
  // (see CriteriaChecklistService); this is the matching UX defense.
  const missingAssessment = item.assessment == null;
  const missingAttestation = !item.reportUploadedAttested;

  const certifyDisabled = missingAssessment || missingAttestation;
  const certifyTooltip = certifyDisabled
    ? missingAssessment && missingAttestation
      ? "Select a Severity Assessment and confirm the report has been uploaded before certifying."
      : missingAssessment
        ? "Select a Severity Assessment in the form before certifying."
        : "Confirm the associated report has been uploaded before certifying."
    : undefined;
  const declineDisabled = missingAttestation;
  const declineTooltip = declineDisabled
    ? "Confirm the associated report has been uploaded before declining."
    : undefined;

  return (
    <li
      className="border border-border-light rounded-md bg-white-pure px-space-3 py-space-2"
      data-testid={`checklist-item-${item.code}`}
      data-status={item.status}
    >
      <div className="flex items-start gap-space-3">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-space-2 mb-space-1">
            <Badge status="draft">{item.code}</Badge>
            <TierBadge tier={item.tier} />
            <StatusBadge status={item.status} />
            {isTerminal && (
              <AdminUndoButton
                label={item.status}
                modalTitle={`Undo Criteria-Item ${item.status}`}
                modalConsequence={
                  <>
                    This returns the item to <strong>Pending</strong> (or
                    <strong> Escalated</strong> if it had an active
                    escalation directive). The audit trail of the prior
                    terminal action is preserved. If a lane assessment has
                    already been issued for this lane, the server will
                    refuse the undo until that issuance is undone first.
                  </>
                }
                successToast={`${item.code} reset from ${item.status}.`}
                invoke={(reason) => undoTerminal.mutateAsync(reason)}
              />
            )}
            {item.activeEscalation && (
              <EscalationOutcomeBadge escalation={item.activeEscalation} />
            )}
            {item.assessment && (
              <AssessmentBadge
                assessment={item.assessment}
                attested={item.reportUploadedAttested}
              />
            )}
          </div>
          <div className="text-small text-onyx font-medium truncate" title={item.title}>
            {item.title}
          </div>
          {catalog?.isCommonExpected && (
            <div className="mt-space-2">
              <CommonExpectedNote
                text={
                  catalog.resolutionGuidance ??
                  "Common-expected per the DQR Reference; calibrate severity in context rather than over-react."
                }
              />
            </div>
          )}
        </div>
        <div className="flex items-center gap-space-1 shrink-0">
          <Button
            variant="tertiary"
            size="sm"
            onClick={() => setFormOpen(true)}
            title={isTerminal ? "View form (read-only)" : "Open form"}
          >
            <FileText size={14} aria-hidden />
            {isTerminal ? "View form" : "Open form"}
          </Button>
          {item.status === "Pending" && actions.certify && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setCertifyOpen(true)}
              disabled={certifyDisabled}
              title={certifyTooltip}
            >
              Certify
            </Button>
          )}
          {item.status === "Pending" && actions.decline && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setDeclineOpen(true)}
              disabled={declineDisabled}
              title={declineTooltip}
            >
              Decline
            </Button>
          )}
          {item.status === "Pending" && actions.escalate && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setEscalateOpen(true)}
            >
              Escalate
            </Button>
          )}
          {waiveOffered && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setWaiveOpen(true)}
            >
              Waive
            </Button>
          )}
          {!readOnly && isAdmin && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setOverflowOpen((o) => !o)}
                className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-border-light hover:bg-white-warm"
                aria-haspopup="menu"
                aria-expanded={overflowOpen}
                aria-label="More actions"
              >
                <MoreVertical size={14} aria-hidden />
              </button>
              {overflowOpen && (
                <div
                  className="absolute right-0 top-9 z-30 min-w-[180px] bg-white-pure border border-border-light rounded-md shadow-md py-space-1"
                  role="menu"
                  onMouseLeave={() => setOverflowOpen(false)}
                >
                  {catalog && (
                    <button
                      type="button"
                      role="menuitem"
                      className="w-full text-left px-space-3 py-space-1 text-small text-onyx hover:bg-white-warm"
                      onClick={() => {
                        setOverflowOpen(false);
                        setEditOpen(true);
                      }}
                    >
                      <FileText size={12} className="inline mr-space-1" aria-hidden />
                      Edit…
                    </button>
                  )}
                  <button
                    type="button"
                    role="menuitem"
                    className="w-full text-left px-space-3 py-space-1 text-small text-status-error hover:bg-white-warm"
                    onClick={() => {
                      setOverflowOpen(false);
                      setResetOpen(true);
                    }}
                  >
                    <Lock size={12} className="inline mr-space-1" aria-hidden />
                    Admin reset…
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {item.status === "Declined" && item.failureReason && (
        <p className="mt-space-2 text-caption text-status-error m-0">
          Decline reason: {item.failureReason}
        </p>
      )}
      {item.status === "Waived" && item.waiverRationale && (
        <p className="mt-space-2 text-caption text-gray-mid italic m-0">
          Waiver rationale: {item.waiverRationale}
        </p>
      )}

      {formOpen && (
        <CriteriaChecklistItemPopover
          open={formOpen}
          onClose={() => setFormOpen(false)}
          dealId={dealId}
          item={item}
          catalog={catalog}
          actions={actions}
        />
      )}
      <CertifyConfirmationModal
        open={certifyOpen}
        onClose={() => setCertifyOpen(false)}
        itemCode={item.code}
        itemTitle={item.title}
        onConfirm={onCertify}
        pending={certify.isPending}
      />
      <DeclineModal
        open={declineOpen}
        onClose={() => setDeclineOpen(false)}
        itemCode={item.code}
        onConfirm={onDecline}
        pending={decline.isPending}
      />
      <EscalateModal
        open={escalateOpen}
        onClose={() => setEscalateOpen(false)}
        itemCode={item.code}
        escalationPath={catalog?.escalationPath ?? null}
        onConfirm={onEscalate}
        pending={escalate.isPending}
      />
      <WaiveModal
        open={waiveOpen}
        onClose={() => setWaiveOpen(false)}
        itemCode={item.code}
        onConfirm={onWaive}
        pending={waive.isPending}
      />
      <AdminResetModal
        open={resetOpen}
        onClose={() => setResetOpen(false)}
        itemCode={item.code}
        onConfirm={onAdminReset}
        pending={reset.isPending}
      />
      {catalog && (
        <CriteriaCatalogEditModal
          open={editOpen}
          onClose={() => setEditOpen(false)}
          catalog={catalog}
        />
      )}
    </li>
  );
}

function TierBadge({ tier }: { tier: CriteriaCatalogItemDto["tier"] }) {
  switch (tier) {
    case "DealKiller":
      return <Badge status="error">Deal Killer</Badge>;
    case "RedFlag":
      return <Badge status="review">Red Flag</Badge>;
    case "NeedsImprovement":
      return <Badge status="info">Needs Improvement</Badge>;
  }
}

function StatusBadge({ status }: { status: CriteriaChecklistItemDto["status"] }) {
  switch (status) {
    case "Pending":
      return <Badge status="draft">Pending</Badge>;
    case "Escalated":
      return <Badge status="review">Escalated</Badge>;
    case "Approved":
      return <Badge status="finalized">Approved</Badge>;
    case "Declined":
      return <Badge status="error">Declined</Badge>;
    case "Waived":
      return <Badge status="gold">Waived</Badge>;
  }
}

function EscalationOutcomeBadge({
  escalation,
}: {
  escalation: ActiveEscalationSummary;
}) {
  // Surface the executive verdict at a glance. Color tokens
  // mirror adjacent chips for visual consistency: Pending → review (amber,
  // same as the ESCALATED status chip); Approved → finalized (green, same
  // as the APPROVED status chip); Denied → error (red, same as the
  // DECLINED status chip).
  const { dateTime } = useDateFormatter();
  const fmt = (iso: string) => dateTime(iso);

  if (escalation.status === "Pending") {
    const tip =
      `Escalation pending review\n` +
      `Escalated by ${escalation.escalatedByUserName} on ${fmt(escalation.escalatedAt)}\n` +
      `Reason: ${escalation.escalationReason}`;
    return (
      <Badge status="review" title={tip}>
        Escalation Pending
      </Badge>
    );
  }

  const resolver = escalation.resolvedByUserName ?? "—";
  const when = escalation.resolvedAt ? fmt(escalation.resolvedAt) : "—";
  const tipBase =
    escalation.status === "Approved"
      ? `Escalation approved by ${resolver} on ${when}`
      : `Escalation denied by ${resolver} on ${when}`;
  const tip =
    `${tipBase}\n` +
    `Directive: ${escalation.resolutionDirective ?? "—"}\n` +
    (escalation.resolutionNotes ? `Notes: ${escalation.resolutionNotes}` : "");

  if (escalation.status === "Approved") {
    return (
      <Badge status="finalized" title={tip}>
        Escalation Approved
      </Badge>
    );
  }
  return (
    <Badge status="error" title={tip}>
      Escalation Denied
    </Badge>
  );
}

function AssessmentBadge({
  assessment,
  attested,
}: {
  assessment: CriteriaItemAssessment;
  attested: boolean;
}) {
  // Severity chip colors map to the closest existing
  // design-system token: Clean→finalized (green), Minor→gold (amber-light),
  // Material→review (warning/orange), Critical→error (red).
  const tone = (() => {
    switch (assessment) {
      case "Clean":
        return "finalized" as const;
      case "Minor":
        return "gold" as const;
      case "Material":
        return "review" as const;
      case "Critical":
        return "error" as const;
    }
  })();
  return (
    <Badge status={tone} title={attested ? "Report uploaded" : undefined}>
      Assessment: {assessment.toUpperCase()}
      {attested && (
        <CheckCircle2 size={10} className="ml-space-1" aria-label="Report uploaded" />
      )}
    </Badge>
  );
}
